import { mcGetItem, mcSetItem } from './mcCloudSync';

export const MC_SECTIONS_KEY = 'mc-sections';
export const MC_SECTION_EVENT = 'mc-section-change';
export const MC_CYCLES_KEY = 'mc-cycles';
export const MC_CYCLE_EVENT = 'mc-cycle-change';

function safeParse(raw, fallback) {
  try { return raw ? JSON.parse(raw) : fallback; } catch { return fallback; }
}

export function normalizeSectionId(value = '') {
  const raw = String(value || '').trim();
  const id = raw
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return id || 'section';
}

export function formatSectionLabel(value = '') {
  const raw = String(value || '').replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!raw) return 'Section';
  return raw.replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function canonicalizeSection(section = {}) {
  const name = String(section?.name || section?.label || formatSectionLabel(section?.id || '')).trim();
  const sectionId = String(section?.sectionId || section?.id || normalizeSectionId(name)).trim() || normalizeSectionId(name);
  const stamp = new Date().toISOString();
  return {
    ...section,
    id: sectionId,
    sectionId,
    name: name || formatSectionLabel(sectionId),
    description: String(section?.description || '').trim(),
    active: section?.active !== false,
    createdAt: section?.createdAt || stamp,
    updatedAt: section?.updatedAt || stamp,
  };
}

export function loadSections() {
  const parsed = safeParse(mcGetItem(MC_SECTIONS_KEY), []);
  const sections = Array.isArray(parsed) ? parsed : Array.isArray(parsed?.sections) ? parsed.sections : [];
  return sections
    .filter(Boolean)
    .map(canonicalizeSection)
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
}

export function saveSections(sections = []) {
  const stamp = new Date().toISOString();
  const byId = new Map();
  (Array.isArray(sections) ? sections : [])
    .filter(Boolean)
    .map((section) => canonicalizeSection({ ...section, updatedAt: stamp }))
    .forEach((section) => {
      byId.set(section.sectionId || section.id || normalizeSectionId(section.name), section);
    });

  const next = Array.from(byId.values()).sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  const previousRaw = mcGetItem(MC_SECTIONS_KEY) || '[]';
  const nextRaw = JSON.stringify(next);
  if (previousRaw !== nextRaw) {
    mcSetItem(MC_SECTIONS_KEY, nextRaw);
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(MC_SECTION_EVENT, { detail: next }));
  }
  return next;
}

// MC479 — if the small canonical sections document was lost, recover section
// identities from surviving operational session/archive metadata. This is bounded
// to the two canonical list authorities already used by Morpheus; it never scans
// Firestore history. We only write when mc-sections is genuinely empty.
export function recoverSectionsFromOperationalHistory() {
  const existing = loadSections();
  if (existing.length) return existing;

  const roots = [
    safeParse(mcGetItem('mc-session-list'), []),
    safeParse(mcGetItem('mc-session-archive'), []),
  ];
  const found = new Map();
  const seen = new Set();
  const walk = (value, depth = 0) => {
    if (!value || depth > 8) return;
    if (typeof value !== 'object') return;
    if (seen.has(value)) return;
    seen.add(value);
    if (Array.isArray(value)) { value.forEach((item) => walk(item, depth + 1)); return; }

    const idRaw = String(value.sectionId || value.section?.sectionId || value.meta?.sectionId || '').trim();
    const nameRaw = String(value.sectionName || value.section?.name || value.meta?.sectionName || '').trim();
    const id = normalizeSectionId(idRaw || nameRaw);
    const token = String(idRaw || nameRaw || '').trim().toLowerCase();
    const fullSite = value.fullSite === true || value.meta?.fullSite === true || ['all','all-sections','full-site','full-count','full-site-count','unassigned','section'].includes(token);
    if ((idRaw || nameRaw) && !fullSite && id && id !== 'section' && id !== 'unassigned') {
      found.set(id, canonicalizeSection({ id, sectionId: id, name: nameRaw || formatSectionLabel(idRaw || id), active: true }));
    }
    Object.values(value).forEach((item) => walk(item, depth + 1));
  };
  roots.forEach((root) => walk(root));
  if (!found.size) return [];
  return saveSections(Array.from(found.values()));
}

export function activeSections() {
  return loadSections().filter((section) => section.active !== false);
}

export function resolveSection(value = '') {
  const target = normalizeSectionId(value);
  return loadSections().find((section) => {
    const candidates = [section.sectionId, section.id, section.name].map(normalizeSectionId);
    return candidates.includes(target);
  }) || null;
}


export function normalizeCycleId(value = '') {
  const raw = String(value || '').trim();
  const id = raw
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return id || 'cycle';
}

export function formatCycleLabel(value = '') {
  const raw = String(value || '').replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!raw) return 'Cycle';
  return raw.replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function canonicalizeCycle(cycle = {}) {
  const hasExplicitName = Object.prototype.hasOwnProperty.call(cycle || {}, 'name');
  const rawName = hasExplicitName ? cycle?.name : (cycle?.label || formatCycleLabel(cycle?.id || ''));
  const name = String(rawName ?? '').trim();
  const cycleId = String(cycle?.cycleId || cycle?.id || normalizeCycleId(name)).trim() || normalizeCycleId(name);
  const stamp = new Date().toISOString();
  return {
    ...cycle,
    id: cycleId,
    cycleId,
    // Keep the display name editable. The default Cycle 1 uses a stable cycleId,
    // so the user can rename it without the value being forced back to Cycle 1 while typing.
    name,
    // Preserve spaces while editing descriptions. Trimming here made the spacebar look broken.
    description: String(cycle?.description ?? ''),
    active: cycle?.active !== false,
    createdAt: cycle?.createdAt || stamp,
    updatedAt: cycle?.updatedAt || stamp,
  };
}

function cycleOrderValue(cycle = {}, index = 0) {
  const explicit = Number(cycle?.sortOrder);
  if (Number.isFinite(explicit)) return explicit;
  const created = Date.parse(cycle?.createdAt || '');
  if (Number.isFinite(created)) return created;
  return index;
}

function sortCyclesByOriginalOrder(cycles = []) {
  return (Array.isArray(cycles) ? cycles : [])
    .map((cycle, index) => ({ cycle, index, order: cycleOrderValue(cycle, index) }))
    .sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order;
      return a.index - b.index;
    })
    .map(({ cycle }, index) => ({ ...cycle, sortOrder: Number.isFinite(Number(cycle?.sortOrder)) ? Number(cycle.sortOrder) : index }));
}

export function loadCycles() {
  const parsed = safeParse(mcGetItem(MC_CYCLES_KEY), []);
  const cycles = Array.isArray(parsed) ? parsed : Array.isArray(parsed?.cycles) ? parsed.cycles : [];
  const normalized = sortCyclesByOriginalOrder(cycles.filter(Boolean).map((cycle, index) => canonicalizeCycle({ ...cycle, sortOrder: cycle?.sortOrder ?? index })));
  if (!normalized.length) {
    return [canonicalizeCycle({ cycleId: 'cycle-1', id: 'cycle-1', name: 'Cycle 1', description: '', active: true, sortOrder: 0, createdAt: '1970-01-01T00:00:00.000Z' })];
  }
  return normalized;
}

export function saveCycles(cycles = []) {
  const stamp = new Date().toISOString();
  const byId = new Map();
  (Array.isArray(cycles) ? cycles : [])
    .filter(Boolean)
    .map((cycle, index) => canonicalizeCycle({ ...cycle, sortOrder: cycle?.sortOrder ?? index, updatedAt: stamp }))
    .forEach((cycle) => {
      byId.set(cycle.cycleId || cycle.id || normalizeCycleId(cycle.name), cycle);
    });

  const next = sortCyclesByOriginalOrder(Array.from(byId.values()));
  const previousRaw = mcGetItem(MC_CYCLES_KEY) || '[]';
  const nextRaw = JSON.stringify(next);
  if (previousRaw !== nextRaw) {
    mcSetItem(MC_CYCLES_KEY, nextRaw);
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(MC_CYCLE_EVENT, { detail: next }));
  }
  return next;
}

export function activeCycles() {
  return loadCycles().filter((cycle) => cycle.active !== false);
}

export function nextCycleName(cycles = loadCycles()) {
  const used = new Set((Array.isArray(cycles) ? cycles : []).map((cycle) => String(cycle?.name || '').trim().toLowerCase()));
  let index = 1;
  while (used.has(`cycle ${index}`)) index += 1;
  return `Cycle ${index}`;
}
